import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/utils/supabase';

const AdminAuthContext = createContext(null);

const isBrowser = typeof window !== 'undefined';

export function AdminAuthProvider({ children }) {
  const [admin, setAdmin]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [isAdmin, setIsAdmin]   = useState(false);

  const checkAdminRole = useCallback(async (user) => {
    if (!user) { setIsAdmin(false); return; }
    const { data } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', user.id)
      .single();
    setIsAdmin(data?.role === 'admin');
  }, []);

  useEffect(() => {
    if (!isBrowser) { setLoading(false); return; }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setAdmin(session?.user ?? null);
      checkAdminRole(session?.user ?? null).finally(() => setLoading(false));
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const user = session?.user ?? null;
      setAdmin(user);
      checkAdminRole(user);
    });

    return () => subscription.unsubscribe();
  }, [checkAdminRole]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    const { data: profile } = await supabase
      .from('user_profiles')
      .select('role')
      .eq('id', data.user.id)
      .single();

    if (profile?.role !== 'admin') {
      await supabase.auth.signOut();
      throw new Error('Access denied. Admin privileges required.');
    }

    setAdmin(data.user);
    setIsAdmin(true);
    return data.user;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setAdmin(null);
    setIsAdmin(false);
  };

  return (
    <AdminAuthContext.Provider value={{ admin, isAdmin, loading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
